import React, { useState, useEffect } from "react";
import styled from "styled-components";

// --- CUSTOM SVG ICONS ---

const HeartIcon = ({ filled = false, ...props }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const EyeIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const ExternalLinkIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
    <polyline points="15 3 21 3 21 9"></polyline>
    <line x1="10" y1="14" x2="21" y2="3"></line>
  </svg>
);

const GithubIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
  </svg>
);

const VideoIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polygon points="23 7 16 12 23 17 23 7"></polygon>
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
  </svg>
);

const ChevronLeftIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="15 18 9 12 15 6"></polyline>
  </svg>
);

const ChevronRightIcon = (props) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="9 18 15 12 9 6"></polyline>
  </svg>
);

// --- STYLED COMPONENTS ---

const PageWrapper = styled.div`
  min-height: 100vh;
  padding: 3rem 5rem;
  background-color: #fff;
  color: #000;
  max-width: 1400px;
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  touch-action: pan-y;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const BackLink = styled.a`
  font-size: 1rem;
  text-decoration: none;
  color: #555;
  margin-bottom: 2rem;
  display: inline-block;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
    color: #000;
  }
`;

const ProjectHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 3rem;
    font-family: "Kaushan Script", cursive;
    margin-bottom: 1rem;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  .short-desc {
    font-size: 1.2rem;
    line-height: 1.6;
    color: #666;
    margin: 0;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const MetricsBar = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  color: #666;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const LikeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background-color: ${props => props.liked ? '#ff6b6b' : '#fff'};
  color: ${props => props.liked ? '#fff' : '#333'};
  border: 2px solid ${props => props.liked ? '#ff6b6b' : '#e0e0e0'};
  border-radius: 30px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &:hover {
    transform: translateY(-2px);
    background-color: ${props => props.liked ? '#ff5252' : '#f9f9f9'};
    border-color: ${props => props.liked ? '#ff5252' : '#ccc'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
  
  svg {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 3rem;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  background-color: #000;
`;

const CarouselWrapper = styled.div`
  width: 100%;
  height: 600px;
  position: relative;
  overflow: hidden;
  border-radius: 16px;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const CarouselSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${props => props.active ? 1 : 0};
  transition: opacity 0.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${props => props.active ? 'auto' : 'none'};
`;

const CarouselImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 20px;' : 'right: 20px;'}
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  pointer-events: auto;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: #000;
    pointer-events: none;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
  pointer-events: auto;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid #fff;
  background-color: ${props => props.active ? '#fff' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  pointer-events: auto;
  
  &:hover {
    background-color: #fff;
  }
`;

const ContentSection = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    font-size: 2rem;
    font-family: "Kaushan Script", cursive;
    margin-bottom: 1rem;
    color: #000;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #444;
    white-space: pre-wrap;

    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 3rem;
`;

const LinkButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.9rem 1.8rem;
  background-color: #000;
  color: #fff;
  text-decoration: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #333;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
  
  svg {
    width: 18px;
    height: 18px;
    pointer-events: none;
  }
`;

const VideoContainer = styled.div`
  margin-bottom: 3rem;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  
  iframe, video {
    width: 100%;
    height: 600px;
    border: none;
    display: block;

    @media (max-width: 768px) {
      height: 300px;
    }
  }
`;

const TechStack = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const TechBadge = styled.span`
  padding: 0.6rem 1.2rem;
  background-color: #f5f5f5;
  color: #333;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 500;
  border: 1px solid #e0e0e0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const InfoCard = styled.div`
  padding: 1.5rem;
  background-color: #fafafa;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  
  h3 {
    font-size: 0.85rem;
    color: #888;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-weight: 600;
  }
  
  p {
    font-size: 1.2rem;
    color: #000;
    font-weight: 600;
    margin: 0;
    text-transform: capitalize;
  }
`;

const LoadingText = styled.p`
  font-size: 1.5rem;
  text-align: center;
  padding: 4rem;
  color: #666;
`;

const ErrorText = styled(LoadingText)`
  color: #ff4444;
`;

// --- REACT COMPONENT ---

const ProjectDetailPage = () => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liking, setLiking] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const getProjectIdFromUrl = () => {
    const path = window.location.pathname;
    const matches = path.match(/\/projects\/([^\/]+)/);
    return matches ? matches[1] : null;
  };

  const projectId = getProjectIdFromUrl();

  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
    } else {
      setError("No project ID found in URL");
      setLoading(false);
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const response = await fetch(`https://portfolio.triodedevs.workers.dev/public/${projectId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const jsonData = await response.json();
      
      if (jsonData && jsonData.data) {
        setProject(jsonData.data);
      } else {
        throw new Error("Data format from API is incorrect.");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (liking || !project) return;
    
    setLiking(true);
    try {
      const response = await fetch(`https://portfolio.triodedevs.workers.dev/public/${projectId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to like project');
      }
      
      const data = await response.json();
      
      setProject(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          like_count: data.like_count
        },
        like_info: {
          ...prev.like_info,
          user_has_liked: data.user_liked
        }
      }));
    } catch (e) {
      console.error('Error liking project:', e);
    } finally {
      setLiking(false);
    }
  };

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (project && project.media.images) {
      setCurrentImageIndex((prev) => 
        prev === project.media.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (project && project.media.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? project.media.images.length - 1 : prev - 1
      );
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  if (loading) {
    return <PageWrapper><LoadingText>Loading project...</LoadingText></PageWrapper>;
  }

  if (error) {
    return <PageWrapper><ErrorText>Error: {error}</ErrorText></PageWrapper>;
  }

  if (!project) {
    return <PageWrapper><ErrorText>Project not found</ErrorText></PageWrapper>;
  }

  const youtubeEmbedUrl = getYouTubeEmbedUrl(project.links.youtube);

  return (
    <PageWrapper>
      <BackLink onClick={() => window.history.back()}>← Back to all projects</BackLink>
      
      <ProjectHeader>
        <h1>{project.title}</h1>
        <p className="short-desc">{project.short_description}</p>
      </ProjectHeader>

      <MetricsBar>
        <MetricItem>
          <EyeIcon />
          <span>{project.metrics.view_count} views</span>
        </MetricItem>
        
        <LikeButton 
          onClick={handleLike} 
          liked={project.like_info.user_has_liked}
          disabled={liking}
        >
          <HeartIcon filled={project.like_info.user_has_liked} />
          <span>{project.metrics.like_count}</span>
        </LikeButton>
      </MetricsBar>

      {project.media.images && project.media.images.length > 0 && (
        <CarouselContainer>
          <CarouselWrapper>
            {project.media.images.map((image, index) => (
              <CarouselSlide key={index} active={index === currentImageIndex}>
                <CarouselImage 
                  src={image} 
                  alt={`${project.title} - ${index + 1}`} 
                  draggable="false"
                />
              </CarouselSlide>
            ))}
            
            {project.media.images.length > 1 && (
              <>
                <CarouselButton direction="left" onClick={prevImage}>
                  <ChevronLeftIcon />
                </CarouselButton>
                <CarouselButton direction="right" onClick={nextImage}>
                  <ChevronRightIcon />
                </CarouselButton>
                
                <DotsContainer>
                  {project.media.images.map((_, index) => (
                    <Dot 
                      key={index} 
                      active={index === currentImageIndex}
                      onClick={() => goToImage(index)}
                    />
                  ))}
                </DotsContainer>
              </>
            )}
          </CarouselWrapper>
        </CarouselContainer>
      )}

      <LinksContainer>
        {project.links.live && (
          <LinkButton href={project.links.live} target="_blank" rel="noopener noreferrer">
            <ExternalLinkIcon />
            View Live Site
          </LinkButton>
        )}
        {project.links.github && (
          <LinkButton href={project.links.github} target="_blank" rel="noopener noreferrer">
            <GithubIcon />
            View on GitHub
          </LinkButton>
        )}
        {project.links.video && !project.links.youtube && (
          <LinkButton href={project.links.video} target="_blank" rel="noopener noreferrer">
            <VideoIcon />
            Watch Video
          </LinkButton>
        )}
      </LinksContainer>

      {youtubeEmbedUrl && (
        <VideoContainer>
          <iframe
            src={youtubeEmbedUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoContainer>
      )}

      {project.links.video && !project.links.youtube && (
        <VideoContainer>
          <video controls>
            <source src={project.links.video} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </VideoContainer>
      )}

      <ContentSection>
        <h2>About This Project</h2>
        <p>{project.description}</p>
      </ContentSection>

      <ContentSection>
        <h2>Technologies Used</h2>
        <TechStack>
          {project.technical.technologies.map((tech, index) => (
            <TechBadge key={index}>{tech}</TechBadge>
          ))}
        </TechStack>
      </ContentSection>

      <InfoGrid>
        <InfoCard>
          <h3>Status</h3>
          <p>{project.technical.status}</p>
        </InfoCard>
        <InfoCard>
          <h3>Category</h3>
          <p>{project.category.name}</p>
        </InfoCard>
        {project.timeline.start_date && (
          <InfoCard>
            <h3>Start Date</h3>
            <p>{new Date(project.timeline.start_date).toLocaleDateString()}</p>
          </InfoCard>
        )}
        {project.timeline.end_date && (
          <InfoCard>
            <h3>End Date</h3>
            <p>{new Date(project.timeline.end_date).toLocaleDateString()}</p>
          </InfoCard>
        )}
      </InfoGrid>
    </PageWrapper>
  );
};

export default ProjectDetailPage;
